// Lightweight Select component using native <select> with shadcn-like styling.
// Uses compound component API for compatibility with shadcn/ui patterns.

import * as React from 'react'
import { cn } from '@/lib/utils'

// --- Types ---

interface SelectProps {
  value: string
  onValueChange: (value: string) => void
  children: React.ReactNode
}

interface SelectTriggerProps {
  className?: string
  children?: React.ReactNode
}

interface SelectValueProps {
  placeholder?: string
}

interface SelectContentProps {
  children: React.ReactNode
}

interface SelectItemProps {
  value: string
  children: React.ReactNode
}

// --- Implementation ---
// Select reads its children tree synchronously to collect placeholder,
// className, and options, then renders a single native <select>.

function SelectImpl({ value, onValueChange, children }: SelectProps) {
  // Walk children to find SelectTrigger (for placeholder) and SelectContent (for options)
  let placeholder = 'Select...'
  const options: Array<{ value: string; label: string }> = []
  let triggerClassName = ''

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    if (child.type === SelectTriggerImpl) {
      const props = child.props as SelectTriggerProps
      if (props.className) triggerClassName = props.className
      // Look for SelectValue inside trigger children
      React.Children.forEach(props.children, (grandchild) => {
        if (React.isValidElement(grandchild) && grandchild.type === SelectValueImpl) {
          const vProps = grandchild.props as SelectValueProps
          if (vProps.placeholder) placeholder = vProps.placeholder
        }
      })
    }
    if (child.type === SelectContentImpl) {
      const props = child.props as SelectContentProps
      collectOptions(props.children, options)
    }
  })

  return (
    <select
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      className={cn(
        'flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none',
        triggerClassName,
      )}
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7394' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 0.75rem center',
        paddingRight: '2.5rem',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

// Marker components — don't render anything, just carry props for SelectImpl to read
function SelectTriggerImpl(_props: SelectTriggerProps) { return null }
function SelectValueImpl(_props: SelectValueProps) { return null }
function SelectContentImpl(_props: SelectContentProps) { return null }
function SelectItemImpl(_props: SelectItemProps) { return null }

/** Recursively collect SelectItem options from children. */
function collectOptions(
  children: React.ReactNode,
  options: Array<{ value: string; label: string }>,
): void {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    if (child.type === SelectItemImpl) {
      const props = child.props as SelectItemProps
      options.push({
        value: props.value,
        label: extractText(props.children),
      })
    }
  })
}

/** Extract plain text from React children. */
function extractText(children: React.ReactNode): string {
  if (typeof children === 'string') return children
  if (typeof children === 'number') return String(children)
  if (!children) return ''
  if (Array.isArray(children)) return children.map(extractText).join('')
  if (React.isValidElement(children)) {
    const props = children.props as Record<string, unknown>
    return extractText(props.children as React.ReactNode)
  }
  return String(children)
}

// Export the implementation components with the expected names
export {
  SelectImpl as Select,
  SelectTriggerImpl as SelectTrigger,
  SelectValueImpl as SelectValue,
  SelectContentImpl as SelectContent,
  SelectItemImpl as SelectItem,
}
